import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { pollPairing, setDeviceToken, startPairing } from './api.js';
import { ADMIN_URL, pairingURL } from './config.js';

const POLL_INTERVAL_MS = 3000;
// Jitter on even the healthy poll. Displays boot together - a power cut ending,
// a wall of screens switched on at opening time - and a fleet polling in
// lockstep arrives as a spike every three seconds instead of a flat trickle.
const POLL_JITTER_MS = 1000;
// What a failing screen climbs to. It never stops trying, because there is
// nobody in the room to reload it, but once a minute is enough.
const MAX_RETRY_MS = 60000;
const RETRY_BASE_MS = 1000;

// What a screen shows before it belongs to an account: a short code the owner
// types into the admin site. The screen polls until the claim lands, then
// stores its long-lived token and reloads as an authenticated display.
export default function PairingScreen({ onPaired }) {
  const [code, setCode] = useState('');
  const [qr, setQr] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // A plain local rather than a ref: StrictMode runs this effect twice, and a
    // ref shared across both runs gets reset to false by the second mount,
    // leaving the first run's poll loop alive and starting a second pairing.
    let cancelled = false;
    let timer;
    // Consecutive failures, and so how far the retry window has opened. Only a
    // poll that actually answers closes it again - a fresh code being issued
    // does not, or a backend handing out codes it then rejects would restart
    // pairing at full speed forever.
    let failures = 0;

    const schedule = (run, ms) => { timer = setTimeout(run, ms); };
    const pollDelay = () => POLL_INTERVAL_MS + Math.random() * POLL_JITTER_MS;
    // Full jitter on a doubling window, capped. Same reasoning as the healthy
    // poll: the point is that a thousand screens do not come back in step.
    const retryDelay = () => Math.random() * Math.min(MAX_RETRY_MS, RETRY_BASE_MS * 2 ** failures);

    async function begin() {
      if (cancelled) return;
      try {
        const started = await startPairing();
        if (cancelled) return;
        setCode(started.code);
        poll(started.code);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        failures += 1;
        // This used to stop here for good, so a display switched on while the
        // backend was down showed an error until somebody power-cycled the
        // television. It keeps trying now, slowly.
        schedule(begin, retryDelay());
      }
    }

    async function poll(pairingCode) {
      if (cancelled) return;
      try {
        const result = await pollPairing(pairingCode);
        if (cancelled) return;

        if (result.pending) {
          failures = 0;
          setError('');
          schedule(() => poll(pairingCode), pollDelay());
          return;
        }
        if (result.token) {
          setDeviceToken(result.token);
          onPaired();
          return;
        }
        // Claimed but no token: the code is spent and will never answer, so
        // start a fresh one rather than polling it until the heat death.
        failures += 1;
        schedule(begin, retryDelay());
      } catch (err) {
        if (cancelled) return;
        // An expired code is not a dead end: start a fresh one. It used to do
        // that immediately, which is a storm and not a retry - two requests
        // per round trip for as long as fresh codes kept failing, measured at
        // 1,400 a minute per display on a 40ms link and 16,000 on a LAN.
        if (err.status === 410 || err.status === 404) {
          failures += 1;
          schedule(begin, retryDelay());
          return;
        }
        setError(err.message);
        failures += 1;
        schedule(() => poll(pairingCode), retryDelay());
      }
    }

    begin();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [onPaired]);

  // The code is rendered as a QR as well as text, so pairing from a phone is a
  // scan rather than transcribing six characters across a room. Generated
  // locally - the code should not travel to a third-party chart service.
  useEffect(() => {
    if (!code) {
      setQr('');
      return undefined;
    }

    let cancelled = false;
    QRCode.toDataURL(pairingURL(code), { margin: 1, width: 512 })
      .then((dataURL) => {
        if (!cancelled) setQr(dataURL);
      })
      .catch(() => {
        // The printed code still works, so a QR failure is not worth showing.
        if (!cancelled) setQr('');
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <main className="app-shell pairing-screen">
      <div className="pairing-card">
        <p className="eyebrow">Litbyt</p>
        <h1>Pair this display</h1>
        <p className="pairing-instructions">
          Scan the code with your phone, or go to {ADMIN_URL.replace(/^https?:\/\//, '')} and
          enter this code, to connect this screen to your account.
        </p>

        <div className="pairing-pair">
          {qr && <img className="pairing-qr" src={qr} alt={`QR code to pair using ${code}`} />}
          <p className="pairing-code">{code || '·······'}</p>
        </div>

        {error && <p className="login-error">{error}</p>}
      </div>
    </main>
  );
}
